import * as ents from '@attain-apps/entities'
import * as store from '@attain-apps/fe/web/main/lib/store'
import { TypeChecker } from '@attain-apps/type-guards'
import * as rxjs from 'rxjs'

import type { Plugin } from '../plugin'

const SUBMIT_CANCELLATION_REASON = {
  batchUnassignNotSupported: 'Batch unassign is not supported yet.',
  noActiveWorkflow: 'No active cluster assignment workflow.',
  noChange: 'No change in cluster assignment.',
  noSelectedRecords: 'No Space records selected for cluster assignment.',
  noScenarioContext: 'No scenario context available.',
} as const

/**
 * Routes submit to assign, single-record unassign, or a no-op cancellation.
 */
export const submit = store.effects.create(
  (feature: Plugin['feature'], plugin: Plugin) =>
    feature.actions.submit.relay(forwardTo =>
      rxjs
        .combineLatest([
          plugin.selectors.assignmentRecords.asObservable,
          plugin.collection.feature.selectors.context.asObservable,
          plugin.selectors.selectedClusterId.asObservable,
        ])
        .pipe(
          rxjs.take(1),
          rxjs.map(([records, context, selectedClusterId]) => {
            if (records.length === 0) {
              return {
                cancellation: {
                  reason: SUBMIT_CANCELLATION_REASON.noSelectedRecords,
                },
              }
            }

            if (!context) {
              return {
                cancellation: {
                  reason: SUBMIT_CANCELLATION_REASON.noScenarioContext,
                },
              }
            }

            // Undefined selection represents "None" in the selector.
            if (TypeChecker.isUndefined(selectedClusterId)) {
              // Unassign is intentionally limited to one record for now.
              // Multi-record "None" submits are explicit cancellations, not
              // silent no-ops.
              if (records.length > 1) {
                return {
                  cancellation: {
                    reason:
                      SUBMIT_CANCELLATION_REASON.batchUnassignNotSupported,
                  },
                }
              }

              const [record] = records

              if (!record || TypeChecker.isUndefined(record.clusterId)) {
                return {
                  cancellation: {
                    reason: SUBMIT_CANCELLATION_REASON.noChange,
                  },
                }
              }

              return forwardTo(feature.actions.unassign, {
                input: {
                  clusterId: record.clusterId,
                  context,
                  recordId: record.id,
                },
                output: () => undefined,
              })
            }

            // Invalid or unchanged selections have no mutation to submit.
            if (!ents.extra.space.Cluster.Id.isId(selectedClusterId)) {
              return {
                cancellation: {
                  reason: SUBMIT_CANCELLATION_REASON.noChange,
                },
              }
            }

            const recordIdsToAssign = records
              .filter(record => record.clusterId !== selectedClusterId)
              .map(record => record.id)

            if (!recordIdsToAssign.length) {
              return {
                cancellation: {
                  reason: SUBMIT_CANCELLATION_REASON.noChange,
                },
              }
            }

            return forwardTo(feature.actions.assign, {
              input: {
                clusterId: selectedClusterId,
                context,
                recordIds: recordIdsToAssign,
              },
              output: () => undefined,
            })
          }),
        ),
    ).asObservable,
)
